import { Icon } from '@iconify/react';
import { useRef, useState } from 'react';
import editFill from '@iconify/icons-eva/edit-fill';
import { Link as RouterLink } from 'react-router-dom';
import trash2Outline from '@iconify/icons-eva/trash-2-outline';
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill';
import {
  Button, IconButton, TableContainer, Table, TableRow, TableBody, TableHead, TableFooter, TableCell,
  Popover, FormControlLabel, Checkbox, FormGroup, Typography, Box, Paper, Stack, MenuItem, TextField
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import plusFill from '@iconify/icons-eva/plus-fill';
//import SettingsIcon from '@mui/icons-material/Settings';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';


// Utility to get a unique key for localStorage
const getLocalStorageKey = (id) => `objectTableColumns_${id}`;

export default function ObjectTable({ id, Data, Columns, onDataState = null, onDataChange = null, PrimaryIdentifier = null, AllowAddRemoveOffset = 0, MaxRows = 9999, ColumnsSelector = null }) {
  const { t } = useTranslation();

  const inputDataFilter = (data) => {
    if (PrimaryIdentifier == ".") {
      return Object.keys(data).map(k => {
        return { name: k, value: data[k] };
      });
    } else {
      return Object.values(data);
    }
  };

  const outDataFilter = (data) => {
    if (PrimaryIdentifier == ".") {
      let d = {};
      Object.values(data || {}).forEach(itm => {
        d[itm.name] = itm.value;
      });
      return d;
    } else {
      return data;
    }
  };

  const [innerData, setInnerData] = useState(inputDataFilter(Data));
  const [dataValid, setDataValid] = useState(true);

  // State for column selector
  const [anchorEl, setAnchorEl] = useState(null); // For Popover
  const [columnVisibility, setColumnVisibility] = useState(() => {
    if (!ColumnsSelector || !id) return {}; // No selector prop or no id, so no special visibility management

    const storedVisibility = localStorage.getItem(getLocalStorageKey(id));
    if (storedVisibility) {
      return JSON.parse(storedVisibility);
    } else {
      // Default from ColumnsSelector if provided
      const initialVisibility = {};
      Object.keys(Columns).forEach(colKey => {
        initialVisibility[colKey] = ColumnsSelector.includes(colKey);
      });
      return initialVisibility;
    }
  });

  const columnsArray = Object.keys(Columns);

  // Effect to update innerData when Data prop changes
  useEffect(() => {
    setInnerData(inputDataFilter(Data));
  }, [Data]);

  // Effect to save column visibility to localStorage
  useEffect(() => {
    if (id && ColumnsSelector) {
      localStorage.setItem(getLocalStorageKey(id), JSON.stringify(columnVisibility));
    }
  }, [columnVisibility, id, ColumnsSelector]);

  const dataIsArray = () => {
    let item_is_array = false;
    if (innerData.length) {
      item_is_array = Array.isArray(innerData[0]);
    } else {
      if (Object.keys(Columns)[0] == "0") {
        if (!Object.values(Columns)[0].Property || Object.values(Columns)[0].Property == "0") {
          item_is_array = true;
        }
      } else {
        if (Object.values(Columns)[0].Property) {
          item_is_array = Object.values(Columns)[0].Property == "0";
        }
      }
    }
    return item_is_array;
  };

  const handleCellChange = (rowid, prop, value, newData, externalSet) => {

    if (prop) {
      prop = String(prop).replace(/^\.$/, 'name').replace(/^\@$/, 'value');
    }

    if (!(rowid === null && prop === null)) {
      if (newData) {
        newData[rowid][prop] = value;
      } else {
		if(innerData[rowid][prop] === value){
			return; 
		}
        innerData[rowid][prop] = value;
      }
    }

    if (!newData)
      newData = innerData.map(a => a);

    setInnerData(newData);

    let piprop = String(PrimaryIdentifier || "").replace(/^\.$/, 'name').replace(/^\@$/, 'value') || null;

    if (onDataChange) {

      let required_array = {};
      Object.keys(Columns).map((cprop, index) => {
        let col = Columns[cprop];
        if (col.Property) {
          cprop = col.Property;
          if (cprop) {
            cprop = String(cprop).replace(/^\.$/, 'name').replace(/^\@$/, 'value');
          }
        }

        if (piprop && cprop === piprop) {
          required_array[cprop] = true;
        } else {
          required_array[cprop] = !!col.required;
        }
      });

      let valid = true;
      if (piprop) {
        Object.values(newData).forEach(item => {
          if (piprop) {
            if (!item[piprop] && String(item[piprop]) !== "0") {
              valid = false;
            }
          }

          if (valid) {
            Object.keys(required_array).map(prop => {
              if (required_array[prop] && (!item[prop] && String(item[prop]) !== "0")) {
                valid = false;
              }
            });
          }
        });
      }

      setDataValid(valid);
	  if(onDataState){
		  try{
			  onDataState({DataValid: valid});
		  }catch(ex){
			  console.error(ex);
		  }
	  }

      if (valid && !externalSet) {
        if (piprop !== null && piprop !== undefined && piprop !== "") {
          let d = {};
          newData.map(itm => {
            d[itm[piprop]] = itm;
          });
          onDataChange(outDataFilter(d), prop, value);
        } else {
          onDataChange(outDataFilter(newData), prop, value);
        }
      }
    }
  };

  useEffect(() => {
    try {
      window["set_" + id + "_table_data"] = (data) => {
        setInnerData(inputDataFilter(data));
      };
    } catch (ex) {
      console.error(ex);
    }
  }, [id]);

  if (Data && Data.length && innerData.length !== Data.length) {
    handleCellChange(null, null, null, Data, true);
  }

  // Column Selector Handlers
  const handleColumnSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColumnSettingsClose = () => {
    setAnchorEl(null);
  };

  const handleColumnVisibilityChange = (colKey) => (event) => {
    setColumnVisibility(prev => ({
      ...prev,
      [colKey]: event.target.checked
    }));
  };

  const openPopover = Boolean(anchorEl);
  const popoverId = openPopover ? 'column-selector-popover' : undefined;

  // Filter columns based on visibility state
  const getVisibleColumns = () => {
    return columnsArray.filter(colKey => {
      return ColumnsSelector ? (columnVisibility[colKey] !== undefined ? columnVisibility[colKey] : false) : true;
    }).map(colKey => ({
      key: colKey,
      ...Columns[colKey]
    }));
  };

  const visibleColumns = getVisibleColumns();

  return (
    <TableContainer component={Paper} className="inline-object-table-wrapper" sx={{ minWidth: 800, position: 'relative' }}>
      {ColumnsSelector && (
        <IconButton
          aria-describedby={popoverId}
          onClick={handleColumnSettingsClick}
          sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
        >
          <ViewColumnIcon />
        </IconButton>
      )}

      <Popover
        id={popoverId}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleColumnSettingsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{t('Select Columns')}</Typography>
          <FormGroup>
            {columnsArray.map(colKey => (
              <FormControlLabel
                key={colKey}
                control={
                  <Checkbox
                    checked={columnVisibility[colKey] || false}
                    onChange={handleColumnVisibilityChange(colKey)}
                    size="small"
                  />
                }
                label={Columns[colKey].HeaderText || colKey}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      <Table id={id} cellPadding={0} cellSpacing={0} className="inline-object-table" size="small">
        <TableHead>
          <TableRow>
            {visibleColumns.map((column, key) => (
              <TableCell key={"otcol-" + column.key} style={column.headerStyle ?? { fontSize: '80%' }} >
                {column.HeaderText}
              </TableCell>
            ))}
            <TableCell sx={{ width: '48px' }}></TableCell> {/* Action column header */}
          </TableRow>
        </TableHead>
        <TableBody className="inline-object-table-body">
          {Object.keys(innerData).map((rowid, key) => { // Using rowid from Object.keys
            let type_array = [];
            let prop_array = [];
            let required_array = [];
            let inputProp_array = [];
            let primary_index = -1;
            let readonly_array = [];

            // Only process properties for visible columns
            visibleColumns.map((col, cindex) => { // Iterate through visibleColumns
              let prop = col.Property;
              if (prop) {
                prop = String(prop).replace(/^\.$/, 'name').replace(/^\@$/, 'value');
                prop_array.push(prop);
              } else {
                prop_array.push(col.key); // Use col.key if no property
              }

              let t = col.Type || "text"
              let inputp = col.inputProps || null;

              try {
                if (typeof t === 'string' && /^function|^\(/.test(t)) {
                  eval('window.__ott_fn1 = ' + t);
                  t = window.__ott_fn1;
				  col.Type = window.__ott_fn1;
                }
              } catch (tex) { 
				console.error(tex); 
			  }

              try {
                if (inputp && typeof inputp === 'string' && /^function|^\(/.test(inputp)) {
                  eval('window.__oti_fn2 = ' + inputp);
                  inputp = window.__oti_fn2;
				  col.inputProps = window.__oti_fn2;
                }
              } catch (tex) { 
				console.error(tex); 
			  }

              type_array.push(t);
              inputProp_array.push(inputp);

              if (PrimaryIdentifier && prop === PrimaryIdentifier) {
                required_array.push(true);
                primary_index = cindex;
              } else {
                required_array.push(col.required || null);
              }

              readonly_array.push(col.readonly || false);
            });

            return (
              <TableRow
                hover
                key={ String(innerData[rowid][PrimaryIdentifier] || rowid) + "-" + String(key) } // Corrected key to use PrimaryIdentifier if available
                tabIndex={-1}
                className='clickable'
              >
                {
                  prop_array.map((prop, index) => { // Iterate through prop_array (which is now only for visible columns)
                    if (prop) {
                      prop = String(prop).replace(/^\.$/, 'name').replace(/^\@$/, 'value');
                    }

                    let type = type_array[index];
                    if (typeof type_array[index] === 'function') {
                      try {
                        type = type_array[index](rowid, innerData[rowid], prop);
                      } catch (t_ex) { console.error(t_ex); }
                    }

                    let inputp = inputProp_array[index];
                    if (typeof inputProp_array[index] === 'function') {
                      try {
                        inputp = inputProp_array[index](rowid, innerData[rowid], prop);
                      } catch (t_ex) { console.error(t_ex); }
                    }
					
					if (type == "checkbox") {
					  if(inputp && inputp.onChange){
						  inputp._onChange = inputp.onChange;
						  delete inputp.onChange;
					  }	
					  return (
                        <TableCell key={"cell_" + String(key) + "_" + String(index)} >
                          <input
                            key={"cellv_" + String(key) + "_" + String(index)}
							id={"cellv_" + String(key) + "_" + String(index)}
                            disabled={readonly_array[index] || (prop == PrimaryIdentifier && AllowAddRemoveOffset >= 0 && key < AllowAddRemoveOffset)}
                            type={"checkbox"}
                            checked={!!innerData[rowid][prop]}
							rowid={rowid}
							prop={prop}
                            value={true}
                            onChange={(e) => {
								handleCellChange(rowid, prop, e.target.checked); 
								if(inputp._onChange) inputp._onChange(e, innerData);
							}}
                            {...inputp} />
                        </TableCell>
                      )
                    } else if (type == "select") {
						
					  if(inputp && inputp.onChange){
						  inputp._onChange = inputp.onChange;
						  delete inputp.onChange;
					  }	
						
                      let options = [];
                      if (inputp && inputp.options) {
                        if (typeof inputp.options === "string" || typeof inputp.options === "function") {
                          if (typeof inputp.options === "string" && /^function|^\(/.test(inputp.options)) {
                            try {
                              eval("(inputp.options = " + inputp.options + ")"); // First eval
                            } catch (eex) {
                              console.error(eex);
                            }
                          }
                          if (inputp.options && typeof inputp.options === 'function') {
                            try {
                              let opt_items = inputp.options(rowid, innerData[rowid], prop); // Second eval (function call)
                              if (opt_items) {
                                if (Array.isArray(opt_items)) {
                                  options = opt_items.map(itm => {
                                    if (typeof itm !== 'object') {
                                      return { name: itm, value: itm };
                                    }
                                    return itm;
                                  });
                                } else if (typeof opt_items === "object") {
                                  options = Object.keys(opt_items).map(k => ({ name: k, value: opt_items[k] }));
                                }
                              }
                            } catch (oex) {
                              console.error(oex);
                            }
                          }
                        } else {
                          options = Object.values(inputp.options).map(itm => {
                            if (typeof itm !== 'object') {
                              return { name: itm, value: itm };
                            }
                            return itm;
                          });
                        }
                      }
                      return (
                        <TableCell key={"cell_" + String(key) + "_" + String(index)} >
                          <select
                            disabled={readonly_array[index] || (prop == PrimaryIdentifier && AllowAddRemoveOffset >= 0 && key < AllowAddRemoveOffset)}
                            key={"cellv_" + String(key) + "_" + String(index)}
							id={"cellv_" + String(key) + "_" + String(index)}
                            value={innerData[rowid][prop]}
							rowid={rowid}
							prop={prop}
                            onChange={(e) => {
								handleCellChange(rowid, prop, e.target.value); 
								if(inputp._onChange) inputp._onChange(e, innerData );
							}}
                            {...inputp} >
                            {
                              options.map((itm, key) => {
                                return (
                                  <option key={String(itm.value) + "-" + String(key)} value={itm.value}>
                                    {itm.name}
                                  </option>
                                )
                              })
                            }
                          </select>
                        </TableCell>
                      )
                    } else if(type == "link"){
						return (
							<TableCell key={"cell_" + String(key) + "_" + String(index)} >
								<Button
								  rowid={rowid}
								  prop={prop}
								  component={RouterLink}
								  to={innerData[rowid][prop]}
								  variant="text"
								  align="center"
								  color="primary"
								  {...inputp}
								>
								{(inputp && inputp.LinkText) ? inputp.LinkText : "→"}
								</Button>
							</TableCell>
						)
					} else {
						
					  if(!inputp){
						  inputp = {};
					  }
					  
					  if(inputp.input_trigger){
						  if(inputp.onInput){
							  inputp._onInput = inputp.onInput;
							  delete inputp.onInput;
						  }
					  }else{
						  if(inputp.onChange){
							  inputp._onChange = inputp.onChange;
							  delete inputp.onChange;
						  }
					  }
					  
					  let to_handle = null;
					  let to_u_handle = null;
					  
					  return (
                        <TableCell key={"cell_" + String(key) + "_" + String(index)} >
                          <input readOnly={readonly_array[index] || (prop == PrimaryIdentifier && AllowAddRemoveOffset >= 0 && key < AllowAddRemoveOffset)}
                            key={"cellv_" + String(key) + "_" + String(index)}
							id={"cellv_" + String(key) + "_" + String(index)}
                            style={required_array[index] ? ((!innerData[rowid][prop] && String(innerData[rowid][prop]) !== "0") ? { border: "1px solid red" } : {}) : null}
                            type={type || "text"}
							rowid={rowid}
							prop={prop}
							disabled={readonly_array[index] || (prop == PrimaryIdentifier && AllowAddRemoveOffset >= 0 && key < AllowAddRemoveOffset)}
                            value={String(innerData[rowid][prop]) || ""}
							{...(inputp.input_trigger ? {
								onInput: (e) => {
								  if(/color|range/.test(type)){
									  if(to_handle) clearTimeout(to_handle);
									  if(to_u_handle) clearTimeout(to_u_handle);
									  to_handle = setTimeout(handleCellChange,150,rowid, prop, e.target.value);
									  if(inputp._onInput) 
										  to_u_handle = setTimeout(inputp._onInput,151,e, innerData);	
								  }else{	
								 	  handleCellChange(rowid, prop, e.target.value); 
									  if(inputp._onInput) inputp._onInput(e, innerData);
								  }
								}} : {
								onChange: (e) => {
								  if(/color|range/.test(type)){
									  if(to_handle) clearTimeout(to_handle);
									  if(to_u_handle) clearTimeout(to_u_handle);
									  to_handle = setTimeout(handleCellChange,150,rowid, prop, e.target.value);
									  if(inputp._onInput) 
										  to_u_handle = setTimeout(inputp._onInput,151,e, innerData);	
								  }else{	
									  handleCellChange(rowid, prop, e.target.value); 
									  if(inputp._onChange) inputp._onChange(e, innerData);
								  }
								}}
							)}
                            {...inputp} />
                        </TableCell>
                      )
                    }
                  })
                }

                {(key >= AllowAddRemoveOffset) ?

                  <TableCell key={"cell_" + String(key) + "_remove"} >
                    <DeleteForeverIcon onClick={(e) => {
                      if (Array.isArray(innerData)) {
                        const newData = [...innerData]; // Create a new array to ensure state update
                        newData.splice(key, 1);
                        handleCellChange(null, null, null, newData); // Pass the new data
                      } else {
                        const newData = { ...innerData };
                        delete newData[rowid];
                        handleCellChange(null, null, null, newData);
                      }
                    }} />
                  </TableCell>
                  :
                  <TableCell key={"cell_" + String(key) + "_remove"} ></TableCell>
                }


              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>

          {
            (!isNaN(AllowAddRemoveOffset) && AllowAddRemoveOffset >= 0) ?
              <TableRow>
                <TableCell key='cmd-object-table-add-cell' colSpan={visibleColumns.length + 1}>
                  <Button
                    className='cmd-object-table-add'
                    key='cmd-object-table-add'
                    color="secondary"
                    size="small"
                    endIcon={<Icon icon={plusFill} />}
                    type="reset"
                    variant="contained"
                    disabled={!dataValid || innerData.length >= MaxRows}
                    onClick={(e) => {
                      if (dataIsArray()) {
                        let itm = [];
                        Object.keys(Columns).forEach(v => {
                          itm.push(null);
                        });

                        let newD = [...innerData, itm];
                        setInnerData(newD);

                        handleCellChange(null, null, null, newD);
                      } else {
                        let uid = t("Item") + (Object.keys(innerData).length + 1);
                        let itm = {};

                        if (PrimaryIdentifier) {
                          itm[PrimaryIdentifier] = uid
                        }

                        Object.keys(Columns).forEach(colKey => {
                          const colDef = Columns[colKey];
                          const propName = colDef.Property ? String(colDef.Property).replace(/^\.$/, 'name').replace(/^\@$/, 'value') : colKey;
                          if (colDef.Default !== undefined) {
                            itm[propName] = colDef.Default;
                          } else if (colDef.Type === 'number') {
                            itm[propName] = 0;
                          } else if (colDef.Type === 'checkbox') {
                            itm[propName] = false;
                          } else {
                            itm[propName] = '';
                          }
                        });

                        let newD = [...innerData, itm];
                        setInnerData(newD);

                        handleCellChange(null, null, null, newD);
                      }
                    }}
                  >
                    {t("Add")}
                  </Button>
                </TableCell>
              </TableRow>
              : null
          }
        </TableFooter>
      </Table>
    </TableContainer>
  );
}